import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import api, { ORDER_FLOW, STATUS_LABELS } from "../../lib/api";
import { play } from "../../lib/sound";

const STATUS_SOUND = {
  rider_assegnato: "rider_assigned", ritirato: "rider_incoming", in_consegna: "rider_arrived",
  consegnato: "delivery_completed", annullato: "order_cancelled",
};
import CustomerHeader from "../../components/CustomerHeader";
import { StatusBadge } from "../../components/Shared";
import LiveMap from "../../components/LiveMap";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Check, Bike, Phone, Star } from "lucide-react";
import { toast } from "sonner";

export default function OrderTracking() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [riderLoc, setRiderLoc] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [reviewed, setReviewed] = useState(false);
  const prevStatus = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/orders/${id}`);
      if (prevStatus.current && data.status !== prevStatus.current && STATUS_SOUND[data.status]) {
        play(STATUS_SOUND[data.status]);
      }
      prevStatus.current = data.status;
      setOrder(data);
    } catch { /* noop */ }
  };
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [id]);
  useEffect(() => {
    const poll = async () => { try { const { data } = await api.get(`/orders/${id}/rider-location`); if (data.assigned) setRiderLoc(data); } catch {} };
    poll(); const t = setInterval(poll, 6000); return () => clearInterval(t);
  }, [id]);
  if (!order) return <div className="min-h-screen bg-[#0A0A0C]"><CustomerHeader /><div className="p-8 text-zinc-500">Caricamento…</div></div>;

  const currentIdx = ORDER_FLOW.indexOf(order.status);
  const cancelled = order.status === "annullato";

  const submitReview = async () => {
    try {
      await api.post("/reviews", { business_id: order.business_id, order_id: order.id, rating, comment });
      setReviewed(true); toast.success("Grazie per la recensione!");
    } catch { toast.error("Errore"); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0C] grain">
      <CustomerHeader />
      <main className="max-w-4xl mx-auto px-4 lg:px-8 py-8 relative z-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display font-black text-3xl tracking-tighter">Ordine #{order.id.slice(-5)}</h1>
            <p className="text-zinc-400">{order.business_name}</p>
          </div>
          <StatusBadge status={order.status} />
        </div>

        {!cancelled ? (
          <div className="bg-[#1C1C1F] border border-white/10 rounded-lg p-6 mb-6">
            <div className="space-y-4">
              {ORDER_FLOW.map((s, i) => {
                const done = i <= currentIdx;
                return (
                  <div key={s} data-testid={`timeline-${s}`} className="flex items-center gap-4">
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${done ? "bg-[#00E676] text-black" : "bg-white/5 text-zinc-600"}`}>
                      {done ? <Check className="h-4 w-4" /> : <span className="text-xs">{i + 1}</span>}
                    </div>
                    <span className={done ? "font-medium" : "text-zinc-600"}>{STATUS_LABELS[s]}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6 mb-6 text-red-400">Ordine annullato.</div>
        )}

        {order.rider_name && (
          <div className="bg-[#1C1C1F] border border-white/10 rounded-lg p-6 mb-6">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><Bike className="h-5 w-5 text-[#00E676]" /> Rider assegnato</h3>
            <div className="flex items-center justify-between">
              <div>
                <span>{order.rider_name}</span>
                {riderLoc?.status && (
                  <span data-testid="rider-live-status" className={`ml-2 text-xs px-2 py-0.5 rounded-full border ${riderLoc.status === "in_consegna" ? "bg-cyan-500/20 text-cyan-400 border-cyan-500/30" : riderLoc.status === "disponibile" ? "bg-[#00E676]/20 text-[#00E676] border-[#00E676]/30" : "bg-zinc-500/20 text-zinc-400 border-zinc-500/30"}`}>
                    {riderLoc.status === "in_consegna" ? "In consegna" : riderLoc.status === "disponibile" ? "Disponibile" : "Offline"}
                  </span>
                )}
              </div>
              <Button variant="secondary" size="sm" data-testid="call-rider" onClick={() => toast.info(`Chiamata al rider ${order.rider_name} in corso… (demo)`)}><Phone className="h-4 w-4 mr-1" /> Contatta</Button>
            </div>
            {["rider_assegnato","ritirato","in_consegna"].includes(order.status) && (
              <div className="mt-4" data-testid="rider-live-map">
                {riderLoc?.eta_min && ["ritirato", "in_consegna"].includes(order.status) && (
                  <div data-testid="rider-eta" className="mb-3 inline-flex items-center gap-2 text-sm rounded-full bg-[#00E676]/10 border border-[#00E676]/30 text-[#00E676] px-3 py-1.5 font-medium">
                    <Bike className="h-4 w-4" /> In arrivo · ~{riderLoc.eta_min} min{riderLoc.remaining_km ? ` · ${riderLoc.remaining_km} km` : ""}
                  </div>
                )}
                <LiveMap height={280} route={riderLoc?.route || []} points={[
                  (order.pickup_lat != null) && { id: "pickup", lat: order.pickup_lat, lng: order.pickup_lng, label: (order.business_name || "Ritiro").slice(0, 18), type: "business" },
                  riderLoc?.location && { id: "rider", lat: riderLoc.location.lat, lng: riderLoc.location.lng, label: "Rider", type: "rider" },
                  (order.dest_lat != null) && { id: "dest", lat: order.dest_lat, lng: order.dest_lng, label: "Consegna", type: "customer" },
                ].filter(Boolean)} />
                {!riderLoc?.location && <div className="text-xs text-zinc-500 mt-2">In attesa della posizione GPS del rider…</div>}
              </div>
            )}
          </div>
        )}

        <div className="bg-[#1C1C1F] border border-white/10 rounded-lg p-6 mb-6">
          <h3 className="font-semibold mb-3">Riepilogo</h3>
          {order.items.map((it, i) => (
            <div key={i} className="flex justify-between text-sm py-1"><span>{it.qty}× {it.name}</span><span>€{(it.price * it.qty).toFixed(2)}</span></div>
          ))}
          <div className="border-t border-white/10 mt-3 pt-3 space-y-1 text-sm text-zinc-400">
            <div className="flex justify-between"><span>Consegna</span><span>€{order.delivery_fee.toFixed(2)}</span></div>
            <div className="flex justify-between"><span>Servizio</span><span>€{order.service_fee.toFixed(2)}</span></div>
            {order.discount > 0 && <div className="flex justify-between text-[#00E676]"><span>Sconto</span><span>-€{order.discount.toFixed(2)}</span></div>}
            {order.payment_status && (
              <div className="flex justify-between"><span>Stato pagamento</span>
                <span data-testid="payment-status" className={
                  order.payment_status === "paid" ? "text-[#00E676]" :
                  order.payment_status === "refunded" ? "text-cyan-400" :
                  order.payment_status === "failed" ? "text-red-400" : "text-amber-400"}>
                  {({ paid: "Pagato", pending: "In attesa", failed: "Fallito", refunded: "Rimborsato" })[order.payment_status] || order.payment_status}
                </span>
              </div>
            )}
            <div className="flex justify-between font-bold text-white text-base pt-1"><span>Totale ({order.payment_method})</span><span className="text-[#00E676]">€{order.total.toFixed(2)}</span></div>
          </div>
          {order.payment_method === "contanti" && (
            <div data-testid="cash-info" className="mt-3 rounded-lg bg-[#00E676]/5 border border-[#00E676]/20 p-3 text-sm">
              <div className="flex justify-between"><span>Da pagare in contanti al rider</span><span className="text-[#00E676] font-semibold">€{order.total.toFixed(2)}</span></div>
              {order.cash_amount_given != null && <div className="flex justify-between text-zinc-400"><span>Paghi con</span><span>€{order.cash_amount_given.toFixed(2)}</span></div>}
              {order.cash_change != null && <div className="flex justify-between text-zinc-400"><span>Resto</span><span>€{order.cash_change.toFixed(2)}</span></div>}
              {order.cash_confirmed && <div className="text-[#00E676] text-xs mt-1" data-testid="cash-confirmed">Incasso confermato dal rider ✓</div>}
            </div>
          )}
        </div>

        {order.status === "consegnato" && !reviewed && (
          <div className="bg-[#1C1C1F] border border-white/10 rounded-lg p-6">
            <h3 className="font-semibold mb-3">Lascia una recensione</h3>
            <div className="flex gap-1 mb-3">
              {[1,2,3,4,5].map((n) => (
                <button key={n} data-testid={`star-${n}`} onClick={() => setRating(n)}><Star className={`h-6 w-6 ${n <= rating ? "fill-[#00E676] text-[#00E676]" : "text-zinc-600"}`} /></button>
              ))}
            </div>
            <Textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Com'è andata?" className="bg-[#0A0A0C] border-white/10 mb-3" />
            <Button data-testid="submit-review" onClick={submitReview} className="bg-[#00E676] text-black rounded-full">Invia recensione</Button>
          </div>
        )}
      </main>
    </div>
  );
}
